import { toast } from '@heidi/design-system/toast';
import { getDefaultStore } from 'jotai';
import { useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';

import { getDeviceButtonCatalog } from '../../data/device-setup/catalogs';
import {
  deviceButtonMappingsAtom,
  dictationTriggerButton,
} from '../../data/device-setup/mappings';
import { dictaphoneTriggerModeAtom } from '../../data/device-setup/trigger-mode';

import { dictaphoneConnectedToastMessages } from './messages';

/** The part of a HID-reader connect event this hook reads. */
export type DictaphoneConnectedEvent = {
  /** False for the devices a reader session finds already connected (boot, flag on, resume). */
  hotPlug: boolean;
};

export type DictaphoneConnectedToastPort<P extends DictaphoneConnectedEvent> = {
  /** Composed device-phase gate (either phase flag, desktop only). */
  enabled: boolean;
  /**
   * Whether saved button mappings reach the reader (the device-setup gate).
   * Off, the reader runs on defaults, so the copy names Record.
   */
  mappingsActive: boolean;
  /** Subscribe to the HID reader's connect event. */
  listen: (cb: (event: { payload: P }) => void) => Promise<() => void>;
  /** The host's device-key formula (`<model>:…`), matching saved mappings. */
  deviceKey: (payload: P) => string;
  /**
   * The listener could not be registered, so no plug-in will toast. The host
   * logs it; never log the device key, which embeds the USB serial.
   */
  onListenError?: (error: unknown) => void;
};

/** USB re-plug churn for the same device confirms once. */
const RECONNECT_DEBOUNCE_MS = 30_000;

/** What the trigger button does right now, read at connect time. */
const connectedBody = (key: string, mappingsActive: boolean) => {
  const store = getDefaultStore();
  const mapping = mappingsActive ? store.get(deviceButtonMappingsAtom)[key] : undefined;
  const buttonId = dictationTriggerButton(mapping);
  if (buttonId === null) {
    return <FormattedMessage {...dictaphoneConnectedToastMessages.noTriggerBody} />;
  }
  const entry = getDeviceButtonCatalog(key.split(':')[0] ?? '').find(
    (candidate) => candidate.id === buttonId
  );
  const button = entry ? <FormattedMessage {...entry.label} /> : buttonId;
  const body =
    store.get(dictaphoneTriggerModeAtom) === 'single_press'
      ? dictaphoneConnectedToastMessages.singlePressBody
      : dictaphoneConnectedToastMessages.doublePressBody;
  return <FormattedMessage {...body} values={{ button }} />;
};

/**
 * Confirm a supported dictaphone being plugged in, on whichever page is open,
 * and say how its button behaves under the user's current settings. Only a
 * real hot-plug counts: a reader session's snapshot of devices already
 * connected (boot, flag on, permission restart) never toasts.
 */
export const useDictaphoneConnectedToast = <P extends DictaphoneConnectedEvent>({
  enabled,
  mappingsActive,
  listen,
  deviceKey,
  onListenError,
}: DictaphoneConnectedToastPort<P>): void => {
  const lastShownRef = useRef(new Map<string, number>());
  // Read at connect time so a mid-session flag flip needs no re-subscribe.
  const mappingsActiveRef = useRef(mappingsActive);
  mappingsActiveRef.current = mappingsActive;
  const onListenErrorRef = useRef(onListenError);
  onListenErrorRef.current = onListenError;

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    let unlisten: (() => void) | undefined;
    const lastShown = lastShownRef.current;

    void listen(({ payload }) => {
      if (cancelled || !payload.hotPlug) return;
      const key = deviceKey(payload);
      const now = Date.now();
      const last = lastShown.get(key);
      if (last !== undefined && now - last < RECONNECT_DEBOUNCE_MS) return;
      lastShown.set(key, now);
      toast({
        tone: 'success',
        title: <FormattedMessage {...dictaphoneConnectedToastMessages.title} />,
        description: connectedBody(key, mappingsActiveRef.current),
      });
    })
      .then((un) => {
        if (cancelled) {
          un();
          return;
        }
        unlisten = un;
      })
      .catch((error: unknown) => {
        if (!cancelled) onListenErrorRef.current?.(error);
      });

    return () => {
      cancelled = true;
      unlisten?.();
    };
  }, [enabled, listen, deviceKey]);
};
